import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DraftingProject, DraftingProjectStatus, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const STATUSES: DraftingProjectStatus[] = [
  'INSTRUCTIONS_RECEIVED',
  'DRAFTING',
  'CONSULTATION',
  'CABINET_CLEARANCE',
  'ASSEMBLY_HANDOFF',
  'ENACTED',
  'WITHDRAWN',
];

const TABS = [
  { key: 'lodge', label: 'Lodge instructions' },
  { key: 'queue', label: 'Drafting queue' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * POST /drafting-projects is open to any authenticated T1+ MDA officer (the
 * instructions-received starting point of the pipeline); GET (the working
 * queue) is LEGISLATIVE_DRAFTER only.
 */
export function Drafting() {
  const { persona } = useAuth();
  const isDrafter = persona?.roles.includes('LEGISLATIVE_DRAFTER') ?? false;
  const [tab, setTab] = useState<TabKey>(isDrafter ? 'queue' : 'lodge');

  return (
    <div>
      <PageHeader title="Legislative Drafting" subtitle="Instructions -> drafts -> consultation -> cabinet -> assembly handoff." />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'lodge' ? <LodgePanel /> : null}
      {tab === 'queue' ? <QueuePanel isDrafter={isDrafter} /> : null}
    </div>
  );
}

function LodgePanel() {
  const { persona } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [instructingMda, setInstructingMda] = useState(persona?.org ?? '');
  const [instructions, setInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<DraftingProject>('/api/v1/m38/drafting-projects', { title, instructingMda, instructions });
      navigate(`/ag/drafting/${created.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to lodge instructions');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="card space-y-3 p-5">
        <Field label="Bill title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Data Protection (Amendment) Bill" />
        </Field>
        <Field label="Instructing MDA">
          <input className="field-input" value={instructingMda} onChange={(e) => setInstructingMda(e.target.value)} />
        </Field>
        <Field label="Instructions">
          <textarea className="field-input" rows={6} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Policy intent and drafting instructions…" />
        </Field>
        <button
          className="btn-primary w-full"
          onClick={submit}
          disabled={busy || title.trim().length < 3 || !instructingMda.trim() || !instructions.trim()}
        >
          {busy ? 'Lodging…' : 'Lodge instructions'}
        </button>
      </div>
    </div>
  );
}

function QueuePanel({ isDrafter }: { isDrafter: boolean }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<DraftingProjectStatus | ''>('');

  const params = new URLSearchParams({ limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  if (status) params.set('status', status);
  const listQ = useApiQuery<Page<DraftingProject>>(isDrafter ? `/api/v1/m38/drafting-projects?${params.toString()}` : null);

  if (!isDrafter) return <ErrorNote message="Requires LEGISLATIVE_DRAFTER — the drafting queue is not visible to this persona." />;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Title / MDA contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as DraftingProjectStatus | '')}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No drafting projects match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Title</th>
                <th className="table-head">Instructing MDA</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ag/drafting/${p.id}`)}>
                  <td className="table-cell font-medium">{p.title}</td>
                  <td className="table-cell text-xs">{p.instructingMda}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
